import type { GroupValue } from '@hive/api';
import { Icon, useLoad, type IconName } from '@hive/ui';
import { unwrap } from '@hive/api';
import type { ReactNode } from 'react';
import { customKindLabel } from '../../lib/assetTypes';
import { useI18nP, useT, type PlatformKey } from '../../i18n';
import { useSession } from '../../lib/session';

/** Ícone por tipo de ativo. */
export function kindIcon(kind: string | undefined): IconName {
  switch (kind) {
    case 'notebook':
    case 'laptop':
      return 'laptop';
    case 'server':
    case 'vm':
    case 'virtual-machine':
      return 'server';
    case 'network':
    case 'switch':
    case 'router':
    case 'firewall':
      return 'layers';
    case 'printer':
    case 'phone':
    case 'mobile':
      return 'box';
    default:
      return 'monitor';
  }
}

/** Rótulo traduzido de um valor da API (tipo de ativo, estado…), caindo para o próprio valor. */
export function useValueLabel() {
  const { t, has } = useI18nP();
  return (prefix: string, v: string | null | undefined): string => {
    if (!v) return '—';
    const own = prefix === 'kind' ? customKindLabel(v) : undefined;
    if (own) return own;
    const k = `${prefix}.${v}`;
    return has(k) ? t(k as PlatformKey) : v;
  };
}

/** Estado do agente com forma e cor (online, atrasado, desligado, sem agente, revogado). */
export function AgentChip({ status }: { status?: string | null }) {
  const t = useT();
  const s = status ?? 'none';
  const cls = s === 'online' ? 'st-online' : s === 'late' ? 'st-atrasado' : s === 'offline' ? 'st-offline' : s === 'revoked' ? 'st-crit' : 'st-sem';
  // R11-08: "replaced" é a entrada antiga de uma máquina que se inscreveu de novo
  const key = (['online', 'late', 'offline', 'revoked', 'replaced'].includes(s) ? `agentSt.${s}` : 'agentSt.none') as PlatformKey;
  return <span className={`st ${cls}`}>{t(key)}</span>;
}

/** Tags do ativo (a API não diz a origem de cada uma: ver lacuna 1 no tracker). */
export function TagList({ tags, max, origin }: { tags?: string[] | null; max?: number; origin?: (tag: string) => 'auto' | 'manual' | null }) {
  const t = useT();
  const list = tags ?? [];
  if (!list.length) return <span className="muted">—</span>;
  const shown = max ? list.slice(0, max) : list;
  return (
    <span className="tags">
      {shown.map((x) => {
        const o = origin?.(x) ?? null;
        return (
          <span key={x} className={`tag${o ? ` ${o}` : ''}`} title={o === 'auto' ? `${x} · ${t('tags.originAuto')}` : o === 'manual' ? `${x} · ${t('tags.originManual')}` : x}>
            {x}
          </span>
        );
      })}
      {max && list.length > max && (
        <span className="muted xs tag-more" title={list.slice(max).join(', ')}>
          +{list.length - max}
        </span>
      )}
    </span>
  );
}

/** Severidade com forma e cor (nunca só cor). */
export function SevPill({ severity }: { severity?: string | null }) {
  const t = useT();
  const s = severity ?? 'info';
  const cls = s === 'critical' ? 'sev-critico' : s === 'major' ? 'sev-alto' : s === 'warning' ? 'sev-aviso' : 'sev-info';
  const key = (['info', 'warning', 'major', 'critical'].includes(s) ? `sev.${s}` : 'sev.info') as PlatformKey;
  return <span className={`sev ${cls}`}>{t(key)}</span>;
}

export const stripeOf = (s?: string | null) =>
  s === 'critical' ? 'stripe-critico' : s === 'major' ? 'stripe-alto' : s === 'warning' ? 'stripe-aviso' : 'stripe-info';

/** Valores de um campo do construtor de blocos (tipos, sites, VLANs, tags…), com contagem de ativos. */
export function useFieldValues(field: string, enabled = true) {
  const s = useSession();
  return useLoad<GroupValue[]>(
    () => (enabled ? unwrap(s.api.GET('/api/v1/groups/fields/{field}/values', { params: { path: { field } } })) : Promise.resolve([])),
    [s.api, field, enabled],
  );
}

/** Departamento lido da tag "Departamento: X" / "Department: X". */
export function deptOf(tags: string[] | null | undefined): string | null {
  for (const x of tags ?? []) {
    const m = /^(departamento|department)\s*:\s*(.+)$/i.exec(x);
    if (m) return m[2]!.trim();
  }
  return null;
}

export function Facts({ items }: { items: Array<[ReactNode, ReactNode]> }) {
  return (
    <div className="facts">
      {items.map(([k, v], i) => (
        <div key={i}>
          <span className="lbl">{k}</span>
          <span>{v}</span>
        </div>
      ))}
    </div>
  );
}

export function KindGlyph({ kind }: { kind?: string }) {
  return (
    <span className="asset-glyph" aria-hidden="true">
      <Icon name={kindIcon(kind)} size="lg" />
    </span>
  );
}

/** Campo de busca das listas da ficha do ativo (software, uso, serviços). */
export function SearchBox({ id, label, value, onChange, placeholder }: { id: string; label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="field search-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type="search" value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
